<script setup>
    import MainHeader from '@/components/HeaderComponent.vue';
    import Pagination from '@/components/PaginationComponent.vue';
    import detail from '@/components/modal/DisclosureCounselingDetailComponent.vue';
    import reserve from '@/components/modal/DisclosureCounselingReserveComponent.vue';
    import userSelect from '@/components/modal/UserSelectComponent.vue';
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useDisclosureCounselingStore } from '@/stores/disclosureCounseling/disclosureCounselingStore';
    import { ref, inject, onMounted } from 'vue';
    import { useRouter } from 'vue-router';

    // 対象者の選択の場合
    const USER_SELECT_TYPE_1 = '1';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    const router = useRouter();
    const commonStore = useCommonStore();
    const disclosureCounselingStore = useDisclosureCounselingStore();

    // 初期化。
    disclosureCounselingStore.disclosureCounselingList = {
        SessionGrpPk: '',
        ApplyDt: '',
        SessionGrpNo: '',
        ServiceNm: '',
        ClientNm: '',
        SessionGrpStatus: '',
        CounselorNm: '',
        SessionGrpRsvDateS: '',
        SessionGrpRsvDateE: '',
    };

    const currentPage = ref(1);
    const totalPages = ref(0);
    const userSelectUseType = ref('');
    const maxPage = 50;

    // 戻るボタン押下時
    function onBack() {
        // ホーム画面へ遷移する。
        router.push({ name: 'home' });
    }

    /**
     * ページ移動
     * @param newPage
     */
    async function handlePageChange(newPage) {
        currentPage.value = newPage;
        await getDisclosureCounselingList();
    }
    const pagination = ref();
    /**
     * ページングの初期処理を実行する。
     * 取得したAPIのデータ数を引き渡す。
     */
    function handleOnloadPagination(RowResult) {
        pagination.value.onload(RowResult);
    }

    // 開示カウンセリング詳細モーダル
    const detailDispFlg = ref('');
    const detailComponent = ref();
    /**
     * 開示カウンセリング詳細モーダルオープン処理
     * @param sessionGroupPk
     */
    function onDisclosureCounselingDetail(sessionGroupPk) {
        detailComponent.value.openModal(commonStore.API_URL, sessionGroupPk);
        detailDispFlg.value = 'block';
    }
    /**
     * 開示カウンセリング詳細モーダルクローズ処理
     */
    async function disclosureCounselingDetailClose() {
        // 開示カウンセリング詳細モーダルを閉じる。
        detailDispFlg.value = '';

        // 1ページ目に戻す。
        currentPage.value = 1;

        // 一覧表を更新する。
        await getDisclosureCounselingList();
    }

    // 開示カウンセリング申込モーダル
    const reserveDispFlg = ref('');
    const reserveComponent = ref();
    /**
     * 開示カウンセリング申込入力モーダルオープン処理
     */
    function onDisclosureCounselingReserve() {
        reserveComponent.value.openModal(commonStore.API_URL);
        reserveDispFlg.value = 'block';
    }
    /**
     * 開示カウンセリング申込モーダルクローズ処理
     */
    function disclosureCounselingReserveClose() {
        reserveDispFlg.value = '';
    }
    /**
     * ユーザー選択モーダルオープン
     */
    function disclosureCounselingUserSelectOpen(type) {
        userSelectUseType.value = type;
        userSelectComponent.value.openModal(commonStore.API_URL, type);
        reserveDispFlg.value = '';
        userSelectDispFlg.value = 'block';
    }

    // ユーザー選択モーダル
    const userSelectDispFlg = ref('');
    const userSelectComponent = ref();
    /**
     * ユーザー選択モーダルクローズ
     */
    function userSelectClose() {
        reserveDispFlg.value = 'block';
        userSelectDispFlg.value = '';
    }
    /**
     * ユーザー選択の選択処理
     * @param UserPk
     * @param UserNm
     */
    function userSelected(UserPk, UserNm) {
        if (USER_SELECT_TYPE_1 === userSelectUseType.value) {
            // ユーザ選択
            reserveComponent.value.selectModal(UserPk, UserNm);
        } else {
            // ユーザー追加
            reserveComponent.value.addModal(UserPk, UserNm);
        }
        reserveDispFlg.value = 'block';
        userSelectDispFlg.value = '';
    }

    // ----------------------------
    // ライフサイクル
    // ----------------------------
    onMounted(async () => {
        await commonStore.getDomain();
        await getDisclosureCounselingList();
    });

    /**
     * 開示カウンセリング一覧取得処理
     */
    async function getDisclosureCounselingList() {
        const disclosureCounselingListRequest = {
            PageCond: {
                RowPosition: maxPage * (currentPage.value - 1) + 1,
                RowAmount: maxPage,
                RowCount: true,
            },
        };
        const response = await disclosureCounselingStore.getDisclosureCounselingListAction(commonStore.API_URL, disclosureCounselingListRequest);
        if (response.success) {
            totalPages.value = disclosureCounselingStore.pageInfo;
            handleOnloadPagination(totalPages.value);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }
    /**
     * date表示変換(YYYY/MM/DD hh:mm:ssをYYYY/MM/DDとして返す。)
     * @param {String} date YYYY/MM/DD hh:mm:ss
     */
    function DateDisp(date) {
        if (date) {
            return date.split(' ')[0];
        }
    }

    /**
     * 開示カウンセリング申込モーダルで登録処理が完了したので、
     * 開示カウンセリング申込モーダルを閉じて、本画面の一覧表を更新する。
     */
    async function disclosureCounselingReserveUpdateAndClose() {
        // 開示カウンセリング申込モーダルを閉じる。
        reserveDispFlg.value = '';

        // 1ページ目に戻す。
        currentPage.value = 1;

        // 一覧表を更新する。
        await getDisclosureCounselingList();
    }
</script>

<template>
    <section id="main">
        <section id="overlay" :style="{ display: detailDispFlg }">
            <detail ref="detailComponent" @disclosureCounselingDetailModal:close="disclosureCounselingDetailClose" />
        </section>
        <!--開示カウンセリング詳細-->
        <section id="overlay" :style="{ display: reserveDispFlg }">
            <reserve ref="reserveComponent" @disclosureCounselingReserveModal:close="disclosureCounselingReserveClose" @disclosureCounselingReserveModal:userSelectOpen="disclosureCounselingUserSelectOpen" @disclosureCounselingReserveModal:updateAndClose="disclosureCounselingReserveUpdateAndClose" />
        </section>
        <!--開示カウンセリング申込-->
        <section id="overlay" :style="{ display: userSelectDispFlg }">
            <userSelect ref="userSelectComponent" @userSelectModal:close="userSelectClose" @userSelectModal:select="userSelected" />
        </section>
        <!--ユーザー選択-->
        <MainHeader />
        <div id="contents">
            <div class="whiteBox wide">
                <h3 class="textCenter">
                    <span>{{ $t('disclosureCounseling.parts.pageTitle1') }}</span>
                </h3>
                <section class="whiteBoxContents">
                    <ul class="btnArea flexLeft">
                        <li>
                            <button class="btnPrimary" id="oepnModalOpenCounselingServiceEntry" @click="onDisclosureCounselingReserve">{{ $t('disclosureCounseling.parts.button1') }}</button>
                        </li>
                    </ul>
                    <article class="innerTable">
                        <table class="table abc">
                            <thead>
                                <tr>
                                    <th style="width: 150px">
                                        <span>{{ $t('disclosureCounseling.parts.tableHeader1') }}</span>
                                    </th>
                                    <th style="width: 150px">
                                        <span>{{ $t('disclosureCounseling.parts.tableHeader2') }}</span>
                                    </th>
                                    <th style="width: 150px">
                                        <span>{{ $t('disclosureCounseling.parts.tableHeader3') }}</span>
                                    </th>
                                    <th style="width: 150px">
                                        <span>{{ $t('disclosureCounseling.parts.tableHeader4') }}</span>
                                    </th>
                                    <th style="width: 150px">
                                        <span>{{ $t('disclosureCounseling.parts.tableHeader5') }}</span>
                                    </th>
                                    <th style="width: 150px">
                                        <span>{{ $t('disclosureCounseling.parts.tableHeader6') }}</span>
                                    </th>
                                    <th style="width: 100%">
                                        <span>{{ $t('disclosureCounseling.parts.tableHeader7') }}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(list, index) in disclosureCounselingStore.disclosureCounselingList" :key="index" class="userDetail" @click="onDisclosureCounselingDetail(list.SessionGrpPk)">
                                    <td>
                                        <span>{{ DateDisp(list.ApplyDt) }}</span>
                                    </td>
                                    <td>
                                        <span>{{ list.SessionGrpNo }}</span>
                                    </td>
                                    <td>
                                        <span>{{ list.ServiceNm }}</span>
                                    </td>
                                    <td>
                                        <span>{{ list.ClientNm }}</span>
                                    </td>
                                    <td>
                                        <span>{{ commonStore.getConvertDictionary('SessionGrpStatus', list.SessionGrpStatus) }}</span>
                                    </td>
                                    <td>
                                        <span>{{ list.CounselorNm }}</span>
                                    </td>
                                    <td>
                                        <span>{{ DateDisp(list.SessionGrpRsvDateS) }}-{{ DateDisp(list.SessionGrpRsvDateE) }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </article>
                    <Pagination ref="pagination" :currentPage="currentPage" :totalPages="totalPages" @pageChanged="handlePageChange" style="margin-bottom: 10px" />
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <button type="button" id="btnBack" class="btnNormal" @click="onBack">{{ $t('common.parts.backPage') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style></style>
